import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save } from "lucide-react";

const empty = {
  cod_partial_enabled: true,
  advance_type: "percent",
  advance_value: 20,
  min_order: 0,
  max_order: 0,
  scope: "all",
  category_ids: [],
  product_ids: [],
  online_discount_enabled: false,
  online_discount_percent: 5,
};

export default function AdminPartialPayments() {
  const [active, setActive] = useState(false);
  const [form, setForm] = useState(empty);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/merchant/config/partial-payments"),
      api.get("/merchant/products").catch(() => ({ data: [] })),
      api.get("/merchant/categories").catch(() => ({ data: [] })),
    ]).then(([{ data }, p, c]) => {
      setActive(Boolean(data.active));
      setForm({ ...empty, ...(data.partial_payments || {}) });
      setProducts(Array.isArray(p.data) ? p.data : []);
      setCategories(Array.isArray(c.data) ? c.data : []);
    }).catch((e) => toast.error(extractErr(e))).finally(() => setLoaded(true));
  }, []);

  const toggleId = (key, id) => {
    const cur = form[key] || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setForm({ ...form, [key]: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/merchant/config/partial-payments", form);
      setForm({ ...empty, ...(data.partial_payments || {}) });
      toast.success("Partial Payments saved");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>Partial Payments</strong> from the App Store to collect a COD advance and offer an online-payment discount.</Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="pp-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Partial Payments</h1>
        <p className="text-sm text-slate-500 mt-1">Collect an advance on COD, and optionally reward shoppers who pay online.</p>
      </div>

      <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pp-cod-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-900">COD partial payment</h2>
            <p className="text-xs text-slate-500 mt-1">When a shopper chooses Cash on Delivery, collect an advance online and the rest at the door.</p>
          </div>
          <Switch checked={Boolean(form.cod_partial_enabled)} onCheckedChange={(v) => setForm({ ...form, cod_partial_enabled: v })} data-testid="pp-cod-enabled" />
        </div>
        {form.cod_partial_enabled && (
          <>
            <div>
              <Label>Advance type</Label>
              <div className="mt-2 flex gap-2">
                {[["percent", "Percentage"], ["fixed", "Fixed amount"]].map(([val, label]) => (
                  <button key={val} type="button" data-testid={`pp-advance-${val}`} onClick={() => setForm({ ...form, advance_type: val })}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.advance_type === val ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>{form.advance_type === "percent" ? "Advance percent" : "Advance amount (₹)"}</Label>
              <Input className="mt-1 max-w-xs" type="number" min="0" step="0.01" data-testid="pp-advance-value" value={form.advance_value} onChange={(e) => setForm({ ...form, advance_value: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Minimum order value</Label>
                <Input className="mt-1" type="number" min="0" data-testid="pp-min-order" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} />
              </div>
              <div>
                <Label>Maximum order value <span className="text-slate-400 font-normal">(0 = no max)</span></Label>
                <Input className="mt-1" type="number" min="0" data-testid="pp-max-order" value={form.max_order} onChange={(e) => setForm({ ...form, max_order: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Apply to</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {[["all", "All products"], ["categories", "Collections"], ["products", "Chosen products"]].map(([val, label]) => (
                  <button key={val} type="button" data-testid={`pp-scope-${val}`} onClick={() => setForm({ ...form, scope: val })}
                    className={`px-3 py-1.5 rounded-full text-sm border ${form.scope === val ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>{label}</button>
                ))}
              </div>
            </div>
            {form.scope === "categories" && (
              <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                {categories.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm px-2 py-1">
                    <input type="checkbox" checked={(form.category_ids || []).includes(c.id)} onChange={() => toggleId("category_ids", c.id)} />
                    {c.name}
                  </label>
                ))}
                {categories.length === 0 && <div className="text-xs text-slate-500 p-2">No collections yet.</div>}
              </div>
            )}
            {form.scope === "products" && (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1" data-testid="pp-product-list">
                {products.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm px-2 py-1">
                    <input type="checkbox" checked={(form.product_ids || []).includes(p.id)} onChange={() => toggleId("product_ids", p.id)} />
                    <span className="truncate">{p.name}</span>
                  </label>
                ))}
                {products.length === 0 && <div className="text-xs text-slate-500 p-2">No products yet.</div>}
              </div>
            )}
          </>
        )}
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70" data-testid="pp-online-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-900">Online payment discount</h2>
            <p className="text-xs text-slate-500 mt-1">Turn this on or off any time. When on, checkout auto-applies X% off and shows a popup.</p>
          </div>
          <Switch checked={Boolean(form.online_discount_enabled)} onCheckedChange={(v) => setForm({ ...form, online_discount_enabled: v })} data-testid="pp-online-enabled" />
        </div>
        {form.online_discount_enabled && (
          <div>
            <Label>Discount percent (X%)</Label>
            <Input className="mt-1 max-w-xs" type="number" min="0" max="90" step="0.1" data-testid="pp-online-percent" value={form.online_discount_percent} onChange={(e) => setForm({ ...form, online_discount_percent: e.target.value })} />
          </div>
        )}
      </Card>

      <Button onClick={save} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="pp-save">
        <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
